import React, { useEffect, useState, useCallback } from 'react';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import API_BASE_URL from '../../api/config';

// --- SVG Icons ---
const SearchIcon = () => (
    <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
);

const SaveIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="16" height="16">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
);

const EmptyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
);

const ListIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="18" height="18">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
);

const GridIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="18" height="18">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
);


// --- UI Components ---
const LoadingSpinner = () => (
    <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading Reports...</p>
    </div>
);

const EmptyState = () => (
    <div className="empty-state-container">
        <EmptyIcon />
        <h3>No Reports Found</h3>
        <p>There are no students assigned to you at the moment.</p>
    </div>
);

const Reports = () => {
    const [mentorName, setMentorName] = useState('');
    const [reports, setReports] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [filteredReports] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [userRole, setUserRole] = useState('');
    
    // New state for toggling views
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'

    useEffect(() => {
        const email = Cookies.get('email');
        const role = Cookies.get('role');

        if (!email) {
            setIsLoading(false);
            return;
        }

        setUserRole(role);
        
        const fetchMentorName = async (email) => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/Facilitator_Panel/read`);
                const data = await res.json();
                const arr = Array.isArray(data) ? data : (data.data || []);
                const mentor = arr.find(
                    (m) => (m.Email || m.email)?.toLowerCase().trim() === email.toLowerCase().trim()
                );

                if (mentor) {
                    setMentorName(mentor.username);
                } else {
                    setIsLoading(false);
                }
            } catch (error) {
                console.error('Error fetching mentor name:', error);
                setIsLoading(false);
            }
        };

        fetchMentorName(email);
    }, []);

    const fetchStudentProfiles = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/profile/read`);
            const data = await res.json();
            const arr = Array.isArray(data) ? data : (data.data || []);
            return mentorName ? arr.filter((s) => (s.Mentors_Name || '').toLowerCase().trim() === mentorName.toLowerCase().trim()) : [];
        } catch (error) {
            console.error('Error fetching student profiles:', error);
            return [];
        }
    }, [mentorName]);

    const fetchReports = useCallback(async (students) => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/Facilitator_Panel/Read_Report`);
            const data = await res.json();
            const arr = Array.isArray(data) ? data : (data.data || []);
            const reportsMap = new Map(arr.map(r => [r.Student_ID, r]));

            const merged = students.map((student) => ({
                Student_ID: student.user_id,
                Student_Name: student.full_name,
                Student_Photo: student.profile_image,
                Chanting: reportsMap.get(student.user_id)?.Chanting || '',
                Sincerity: reportsMap.get(student.user_id)?.Sincerity || '',
                Need: reportsMap.get(student.user_id)?.Need || '',
                Interest: reportsMap.get(student.user_id)?.Interest || '',
                Concern: reportsMap.get(student.user_id)?.Concern || '',
                Book_Completed: reportsMap.get(student.user_id)?.Book_Completed || '',
                Book_Alloted: reportsMap.get(student.user_id)?.Book_Alloted || '',
                Course_Completed: reportsMap.get(student.user_id)?.Course_Completed || '',
                Course_Enrolled: reportsMap.get(student.user_id)?.Course_Enrolled || '',
                Remarks: reportsMap.get(student.user_id)?.Remarks || '',
                Last_Report: reportsMap.get(student.user_id)?.Last_Report || ''
            }));
            setReports(merged);
        } catch (error) {
            console.error('Error fetching report data:', error);
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (!mentorName) return;
            try {
                setIsLoading(true);
                const students = await fetchStudentProfiles();
                await fetchReports(students);
            } catch (error) {
                console.error("Report loading error:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [mentorName, fetchStudentProfiles, fetchReports]);

    const handleInputChange = (studentId, field, value) => {
        const updatedReports = reports.map(r =>
            r.Student_ID === studentId ? { ...r, [field]: value } : r
        );
        setReports(updatedReports);
    };

    // Centralized Save logic to avoid repeating date formatting
    const processAndSaveReport = (report) => {
        const options = {
            timeZone: 'Asia/Kolkata', 
            day: '2-digit', month: '2-digit', year: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false
        };
        const formattedDateTime = new Date().toLocaleString('en-GB', options).replace(',', '');
        handleInputChange(report.Student_ID, 'Last_Report', formattedDateTime);
        
        // Pass the updated report directly to avoid state batching delays
        handleSave({ ...report, Last_Report: formattedDateTime });
    };

    const handleSave = async (report) => {
        const result = await Swal.fire({
            title: 'Confirm Changes',
            text: `Are you sure you want to save the report for ${report.Student_Name}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, save it!',
            customClass: {
                popup: 'swal2-popup',
                title: 'swal2-title',
                htmlContainer: 'swal2-html-container',
                confirmButton: 'swal2-confirm',
                cancelButton: 'swal2-cancel'
            }
        });

        if (!result.isConfirmed) return;

        try {
            const response = await fetch(`${API_BASE_URL}/api/Facilitator_Panel/Update_Report`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(report),
            });

            const resData = await response.json();

            if (resData.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Saved!',
                    text: resData.message,
                    timer: 2000,
                    showConfirmButton: false,
                });
            } else {
                throw new Error(resData.message || 'Failed to update report.');
            }
        } catch (error) {
            console.error('Update error:', error);
            Swal.fire({
                icon: 'error',
                title: 'An Error Occurred',
                text: `Could not save the report. Details: ${error.message}`,
            });
        }
    };

    return (
        <div className="report_area_main">
            {userRole === 'facilitator' && (
                <div className="data-container">
                    <div className="container-header">
                        <h1 className="header-title">Student Reports</h1>
                        
                        <div className="header-actions">
                            <div className="search-box">
                                <SearchIcon />
                                <input
                                    className="search-input"
                                    type="text"
                                    placeholder="Search by name..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </div>
                            
                            {/* Toggle Buttons */}
                            <div className="view-toggle-container">
                                <button 
                                    className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                                    onClick={() => setViewMode('table')}
                                    title="Table View"
                                >
                                    <ListIcon />
                                </button>
                                <button 
                                    className={`view-toggle-btn ${viewMode === 'card' ? 'active' : ''}`}
                                    onClick={() => setViewMode('card')}
                                    title="Card View"
                                >
                                    <GridIcon />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="table-wrapper">
                        {isLoading ? (
                            <LoadingSpinner />
                        ) : filteredReports.length > 0 ? (
                            
                            viewMode === 'table' ? (
                                /* --- TABLE VIEW --- */
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Student</th>
                                            <th>Chanting</th>
                                            <th>Sincerity</th>
                                            <th>Need</th>
                                            <th>Interest</th>
                                            <th>Concern</th>
                                            <th>Books Completed</th>
                                            <th>Books Alloted</th>
                                            <th>Courses Completed</th>
                                            <th>Courses Enrolled</th>
                                            <th>Remarks</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredReports.map((report) => (
                                            <tr key={report.Student_ID}>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <img
                                                            src={report.Student_Photo ? `https://ethiccraft.org/${report.Student_Photo}` : `https://ui-avatars.com/api/?name=${report.Student_Name}&background=random`}
                                                            alt={report.Student_Name}
                                                            className="profile-image-small"
                                                        />
                                                        <span style={{ color: '#000' }}>{report.Student_Name}</span>
                                                    </div>
                                                </td>
                                                <td><input type="text" value={report.Chanting} onChange={(e) => handleInputChange(report.Student_ID, 'Chanting', e.target.value)} className="input-cell" /></td>
                                                <td><input type="text" value={report.Sincerity} onChange={(e) => handleInputChange(report.Student_ID, 'Sincerity', e.target.value)} className="input-cell" /></td>
                                                <td><input type="text" value={report.Need} onChange={(e) => handleInputChange(report.Student_ID, 'Need', e.target.value)} className="input-cell" /></td>
                                                <td><input type="text" value={report.Interest} onChange={(e) => handleInputChange(report.Student_ID, 'Interest', e.target.value)} className="input-cell" /></td>
                                                <td><input type="text" value={report.Concern} onChange={(e) => handleInputChange(report.Student_ID, 'Concern', e.target.value)} className="input-cell" /></td>
                                                <td><input type="text" value={report.Book_Completed} onChange={(e) => handleInputChange(report.Student_ID, 'Book_Completed', e.target.value)} className="input-cell" /></td>
                                                <td><input type="text" value={report.Book_Alloted} onChange={(e) => handleInputChange(report.Student_ID, 'Book_Alloted', e.target.value)} className="input-cell" /></td>
                                                <td><input type="text" value={report.Course_Completed} onChange={(e) => handleInputChange(report.Student_ID, 'Course_Completed', e.target.value)} className="input-cell" /></td>
                                                <td><input type="text" value={report.Course_Enrolled} onChange={(e) => handleInputChange(report.Student_ID, 'Course_Enrolled', e.target.value)} className="input-cell" /></td>
                                                <td><input type="text" value={report.Remarks} onChange={(e) => handleInputChange(report.Student_ID, 'Remarks', e.target.value)} className="input-cell" /></td>
                                                <td>
                                                    <button onClick={() => processAndSaveReport(report)} className="btn btn-save">
                                                        <SaveIcon /> Save
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                /* --- CARD VIEW --- */
                                <div className="card-grid">
                                    {filteredReports.map((report) => (
                                        <div className="student-card" key={report.Student_ID}>
                                            <div className="card-banner-bg"></div>
                                            <div className="card-content-wrapper">
                                                <div className="card-profile-section">
                                                    <img
                                                        src={report.Student_Photo ? `https://ethiccraft.org/${report.Student_Photo}` : `https://ui-avatars.com/api/?name=${report.Student_Name}&background=random`}
                                                        alt={report.Student_Name}
                                                        className="card-avatar"
                                                    />
                                                </div>
                                                <div className="card-identity">
                                                    <h3 className="card-name">{report.Student_Name}</h3>
                                                    <p className="last-updated">Last Updated: {report.Last_Report || 'Never'}</p>
                                                </div>
                                                
                                                <div className="report-edit-grid">
                                                    <div className="edit-group">
                                                        <label>Chanting</label>
                                                        <input type="text" value={report.Chanting} onChange={(e) => handleInputChange(report.Student_ID, 'Chanting', e.target.value)} className="card-input" placeholder="e.g. 16 Rounds"/>
                                                    </div>
                                                    <div className="edit-group">
                                                        <label>Sincerity (%)</label>
                                                        <input type="number" value={report.Sincerity} onChange={(e) => handleInputChange(report.Student_ID, 'Sincerity', e.target.value)} className="card-input" placeholder="0-100"/>
                                                    </div>
                                                    <div className="edit-group full-width">
                                                        <label>Need</label>
                                                        <input type="text" value={report.Need} onChange={(e) => handleInputChange(report.Student_ID, 'Need', e.target.value)} className="card-input" placeholder="What does the student need?"/>
                                                    </div>
                                                    <div className="edit-group full-width">
                                                        <label>Interest</label>
                                                        <input type="text" value={report.Interest} onChange={(e) => handleInputChange(report.Student_ID, 'Interest', e.target.value)} className="card-input" placeholder="What are they interested in?"/>
                                                    </div>
                                                    <div className="edit-group full-width">
                                                        <label>Concern</label>
                                                        <input type="text" value={report.Concern} onChange={(e) => handleInputChange(report.Student_ID, 'Concern', e.target.value)} className="card-input" placeholder="Any concerns?"/>
                                                    </div>
                                                    <div className="edit-group full-width">
                                                        <label>Books Completed</label>
                                                        <input type="text" value={report.Book_Completed} onChange={(e) => handleInputChange(report.Student_ID, 'Book_Completed', e.target.value)} className="card-input" placeholder="Books completed"/>
                                                    </div>
                                                    <div className="edit-group full-width"> 
                                                        <label>Books Alloted</label>
                                                        <input type="text" value={report.Book_Alloted} onChange={(e) => handleInputChange(report.Student_ID, 'Book_Alloted', e.target.value)} className="card-input" placeholder="Books alloted"/>
                                                    </div>
                                                    <div className="edit-group full-width">
                                                        <label>Courses Completed</label>
                                                        <input type="text" value={report.Course_Completed} onChange={(e) => handleInputChange(report.Student_ID, 'Course_Completed', e.target.value)} className="card-input" placeholder="Courses completed"/>
                                                    </div>
                                                    <div className="edit-group full-width">
                                                        <label>Courses Enrolled</label>
                                                        <input type="text" value={report.Course_Enrolled} onChange={(e) => handleInputChange(report.Student_ID, 'Course_Enrolled', e.target.value)} className="card-input" placeholder="Courses enrolled"/>
                                                    </div>
                                                    <div className="edit-group full-width">
                                                        <label>Remarks</label>
                                                        <textarea value={report.Remarks} onChange={(e) => handleInputChange(report.Student_ID, 'Remarks', e.target.value)} className="card-input textarea" rows="2" placeholder="Additional remarks..."></textarea>
                                                    </div>
                                                </div>

                                                <div className="card-footer-actions">
                                                    <button onClick={() => processAndSaveReport(report)} className="btn-action-fill">
                                                        <SaveIcon /> Save Report
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )

                        ) : (
                            <EmptyState />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Reports;